import React from 'react';
import { Image } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import * as accessbilityRoles from '../../../modules/accessbilityRoles';
import { SITE_ROOT } from '../../../config';
import { getIsApp } from '../../../modules/AppInfo';
import { addQueryStringParams } from '../../../modules/url';
import {
  Stack,
  BodyText,
  IBodyTextProps,
} from '../../ui';
import { UtmParams } from '../../../hooks/useGetUtmParams';
import { SUPPORT_PAGE_URL } from '../../../modules/SupportServiceWrapper';

const FacebookImage = require('../../../../images/footer_facebook.png');
const TwitterImage = require('../../../../images/footer_twitter.png');
const PinterestImage = require('../../../../images/footer_pinterest.png');
const InstagramImage = require('../../../../images/footer_instagram.png');
const AppleImage = require('../../../../images/appstore_outline_white.png');
const AndroidImage = require('../../../../images/googleplay_outline_white.png');

type LinkProps = IBodyTextProps & {
  href: string;
  target?: string;
  title?: string;
};
/**
 * Renders an anchor for linking to various locations
 * Note that a React Native Web Text component can handle links but really shouldn't
 */
const Link: React.FCWithChildren<LinkProps> = ({
  children, style, ...restProps
}) => (
  <BodyText
    accessibilityRole="link"
    color={BrandColor.Silver}
    style={{
      lineHeight: 14,
      ...style,
    }}
    {...restProps}
  >
    {children}
  </BodyText>
);

/**
 * The equivalent of `<li><a></a></li>`
 */
const LinkBlock: React.FCWithChildren<LinkProps> = ({ children, ...restProps }) => (
  <Inline>
    <Link {...restProps}>
      {children}
    </Link>
  </Inline>
);

type FooterProps = {
  utmParams?: UtmParams;
};

/**
 * Website footer. Displays a grouped list of links. Only visible at desktop width.
 */
const Footer: React.FCWithChildren<FooterProps> = ({ utmParams }) => (
  <Box
    testID="desktop-footer"
    color={BrandColor.BackInBlack}
    paddingTop={28}
    paddingBottom={20}
    paddingX={60}
    alignItems="center"
  >
    <Inline
      space={20}
      maxWidth={1036}
    >
      <Stack
        flexGrow={2}
        flexShrink={2}
        space={20}
      >
        {/* @ts-ignore */}
        <Stack accessibilityRole={accessbilityRoles.HeaderContainer} space={8}>
          <BodyText
            // @ts-ignore
            accessibilityRole={accessbilityRoles.HeaderText}
            aria-level="2"
            color={BrandColor.WhiteWash}
            style={{
              fontSize: 16,
            }}
            textTransform="uppercase"
          >
            About StyleSeat
          </BodyText>
          <BodyText color={BrandColor.Silver}>
            StyleSeat is the online destination for beauty &amp; wellness professionals
            and clients. Professionals can showcase their work, connect with new and
            existing clients, and build their business. Clients can discover new
            services and providers, book appointments online, and get inspired.
          </BodyText>
        </Stack>
        {getIsApp() ? null : (
          <Inline testID="app-links" space={8}>
            <LinkBlock
              href="https://itunes.apple.com/us/app/styleseat-hair-nails-massage/id414313281?mt=8"
              title="Download on the App Store"
            >
              <Image source={AppleImage} style={{ width: 145, height: 50 }} />
            </LinkBlock>
            <LinkBlock
              href="https://play.google.com/store/apps/details?id=com.styleseat.promobile&hl=en"
              title="Android App on Google Play"
            >
              <Image source={AndroidImage} style={{ width: 170, height: 50 }} />
            </LinkBlock>
          </Inline>
        )}
        <Inline>
          <BodyText appearance="medium" color={BrandColor.Silver}>
            &copy; {new Date().getFullYear()} StyleSeat, Inc. All rights reserved.
          </BodyText>
        </Inline>
      </Stack>
      <Stack
        space={12}
        flexGrow={1}
        flexShrink={0}
        paddingRight={20}
      >
        <Stack testID="Footer-mediaSection" space={8}>
          <BodyText
            // @ts-ignore
            accessibilityRole={accessbilityRoles.HeaderText}
            aria-level="2"
            color={BrandColor.WhiteWash}
            style={{
              fontSize: 16,
            }}
            textTransform="uppercase"
          >
            Media
          </BodyText>
          <LinkBlock href={addQueryStringParams(`${SITE_ROOT}/blog/`, utmParams)} target="_blank">StyleSeat Blog</LinkBlock>
          <LinkBlock href={addQueryStringParams('http://press.styleseat.com/', utmParams)} target="_blank">Press</LinkBlock>
        </Stack>
        <Stack space={8}>
          <BodyText
            // @ts-ignore
            accessibilityRole={accessbilityRoles.HeaderText}
            aria-level="2"
            color={BrandColor.WhiteWash}
            style={{
              fontSize: 16,
            }}
            textTransform="uppercase"
          >
            Talk to us
          </BodyText>
          <LinkBlock
            testID="footer-support-link"
            href={addQueryStringParams(SUPPORT_PAGE_URL, utmParams)}
            target="_blank"
          >
            StyleSeat Help Center
          </LinkBlock>
        </Stack>
      </Stack>
      <Stack space={20} flexGrow={1} flexShrink={0}>
        <Stack space={12}>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams(`${SITE_ROOT}/search`, utmParams)}>Find Professionals</LinkBlock>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams(`${SITE_ROOT}/join`, utmParams)} target="_self">Get Listed</LinkBlock>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams('https://mss.styleseat.com/aboutus/', utmParams)} target="_self">Team</LinkBlock>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams(`${SITE_ROOT}/careers`, utmParams)} target="_self">Careers</LinkBlock>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams(`${SITE_ROOT}/tos-for-professionals`, utmParams)} target="_self">Terms for Pros</LinkBlock>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams(`${SITE_ROOT}/tos-for-clients`, utmParams)} target="_self">Terms for Clients</LinkBlock>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams(`${SITE_ROOT}/privacy`, utmParams)} target="_self">Privacy</LinkBlock>
          <LinkBlock textTransform="uppercase" href={addQueryStringParams(`${SITE_ROOT}/sitemap`, utmParams)} target="_self">Sitemap</LinkBlock>
        </Stack>
        <Inline space={4}>
          <LinkBlock href="https://www.facebook.com/styleseat" target="_blank" title="Facebook">
            <Image source={FacebookImage} style={{ height: 30, width: 30 }} />
          </LinkBlock>
          <LinkBlock href="https://twitter.com/StyleSeat" target="_blank" title="Twitter">
            <Image source={TwitterImage} style={{ height: 30, width: 30 }} />
          </LinkBlock>
          <LinkBlock href="https://www.pinterest.com/styleseat/" target="_blank" title="Pinterest">
            <Image source={PinterestImage} style={{ height: 30, width: 30 }} />
          </LinkBlock>
          <LinkBlock href="https://www.instagram.com/styleseat/" target="_blank" title="Instagram">
            <Image source={InstagramImage} style={{ height: 30, width: 30 }} />
          </LinkBlock>
        </Inline>
      </Stack>
    </Inline>
  </Box>
);

export default Footer;
